import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MAP_TYPES } from '../lib/sampleDataGenerator.js';
import MapView from '../components/MapView.jsx';
import { createDemo } from '../lib/api.js';
import { BASEMAPS, DEFAULT_BASEMAP } from '../lib/basemaps.js';
import { CITIES_BY_STATE, STATE_ORDER, STATE_META, findCityBySlug } from '../lib/cityPresets.js';
import AliCommands from '../components/AliCommands.jsx';

export default function Create() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialType = params.get('type') && MAP_TYPES[params.get('type')] ? params.get('type') : 'crash_safety';

  const [mapType, setMapType] = useState(initialType);
  const [org, setOrg] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [stateCode, setStateCode] = useState('VA');
  const [citySlug, setCitySlug] = useState('martinsville');
  const [lat, setLat] = useState('36.6916');
  const [lon, setLon] = useState('-79.8725');
  const [locLabel, setLocLabel] = useState('Martinsville, VA');
  const [basemap, setBasemap] = useState(DEFAULT_BASEMAP);
  const [radiusScale, setRadiusScale] = useState(1); // 0.25× – 3× of the type's default
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const latNum = parseFloat(lat);
  const lonNum = parseFloat(lon);
  const coordsValid = Number.isFinite(latNum) && Number.isFinite(lonNum)
    && latNum >= -90 && latNum <= 90 && lonNum >= -180 && lonNum <= 180;

  const effectiveRadius = +(MAP_TYPES[mapType].defaultRadiusMiles * radiusScale).toFixed(3);
  const previewSeed = useMemo(() => `${mapType}:${lat}:${lon}:preview`, [mapType, lat, lon]);
  const selectedCity = useMemo(() => findCityBySlug(citySlug), [citySlug]);
  const previewData = useMemo(() => {
    if (!coordsValid) return null;
    const bbox = selectedCity ? selectedCity.bbox : null;
    return MAP_TYPES[mapType].generator(latNum, lonNum, effectiveRadius, previewSeed, bbox);
  }, [mapType, latNum, lonNum, coordsValid, previewSeed, effectiveRadius, selectedCity]);

  const canGenerate = org.trim().length > 0 && title.trim().length > 0 && coordsValid && !submitting;

  useEffect(() => {
    if (params.get('type') && MAP_TYPES[params.get('type')]) setMapType(params.get('type'));
  }, [params]);

  async function handleGenerate(e) {
    e.preventDefault();
    if (!canGenerate) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const saved = await createDemo({
        mapType,
        org: org.trim(),
        title: title.trim(),
        notes: notes.trim(),
        locLabel: locLabel.trim() || `${latNum.toFixed(4)}, ${lonNum.toFixed(4)}`,
        lat: latNum,
        lon: lonNum,
        basemap,
        radiusMiles: effectiveRadius,
        state: selectedCity?.state || null,
        citySlug: selectedCity?.slug || null,
        cityName: selectedCity?.name || null,
        bbox: selectedCity?.bbox || null,
      });
      navigate(`/d/${saved.shortId}`);
    } catch (e) {
      setSubmitError(e.message || 'Failed to create demo.');
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
      <form onSubmit={handleGenerate} className="card p-5 space-y-5" aria-label="Demo map configuration">
        <div>
          <h1 className="text-lg font-semibold text-atlas-ink">Build your demo map</h1>
          <p className="text-sm text-atlas-muted">Ali can build the whole thing for you — or fill in the basics manually below.</p>
        </div>

        <AliCommands
          mapType={mapType} setMapType={setMapType}
          stateCode={stateCode} setStateCode={setStateCode}
          citySlug={citySlug} setCitySlug={setCitySlug}
          setLat={setLat} setLon={setLon} setLocLabel={setLocLabel}
          radiusScale={radiusScale} setRadiusScale={setRadiusScale}
          setOrg={setOrg} setTitle={setTitle} setNotes={setNotes}
          selectedCity={selectedCity}
        />

        <fieldset>
          <legend className="text-sm font-medium text-atlas-ink mb-2">Map type</legend>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(MAP_TYPES).map(([key, t]) => {
              const selected = key === mapType;
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => setMapType(key)}
                  aria-pressed={selected}
                  className={`text-left p-3 rounded-md border text-sm transition-colors ${
                    selected
                      ? 'border-atlas-gold bg-atlas-gold/10 text-atlas-ink'
                      : 'border-atlas-line bg-white text-atlas-ink hover:bg-atlas-paper-2'
                  }`}
                >
                  <div className="font-medium">{t.label}</div>
                  <div className="text-xs text-atlas-muted mt-0.5">{t.defaultRadiusMiles} mi radius</div>
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="space-y-3">
          <Field id="org" label="Organization name" value={org} onChange={setOrg} required maxLength={80} />
          <Field id="title" label="Project title" value={title} onChange={setTitle} required maxLength={80} />

          <fieldset>
            <legend className="text-sm font-medium text-atlas-ink mb-2">
              Choose a city
              <span className="text-xs text-atlas-muted font-normal ml-2">
                (guaranteed on-land — bbox clipped)
              </span>
            </legend>
            <div className="flex flex-wrap gap-1 mb-2">
              {STATE_ORDER.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setStateCode(s);
                    const first = CITIES_BY_STATE[s][0];
                    if (first) {
                      setCitySlug(first.slug);
                      setLat(String(first.lat));
                      setLon(String(first.lon));
                      setLocLabel(`${first.name}, ${s}`);
                    }
                  }}
                  aria-pressed={stateCode === s}
                  className={`text-xs font-semibold px-2.5 py-1 rounded border transition-colors ${
                    stateCode === s
                      ? 'text-white border-transparent'
                      : 'bg-white text-atlas-ink border-atlas-line hover:bg-atlas-paper-2'
                  }`}
                  style={stateCode === s ? { backgroundColor: STATE_META[s].accent } : {}}
                >
                  {s}
                </button>
              ))}
            </div>
            <select
              value={citySlug}
              onChange={(e) => {
                const slug = e.target.value;
                setCitySlug(slug);
                const c = findCityBySlug(slug);
                if (c) {
                  setLat(String(c.lat));
                  setLon(String(c.lon));
                  setLocLabel(`${c.name}, ${c.state}`);
                }
              }}
              className="w-full border border-atlas-line rounded-md px-3 py-2 text-sm bg-white"
            >
              {(CITIES_BY_STATE[stateCode] || []).map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <p className="text-[11px] text-atlas-muted mt-1">
              {selectedCity
                ? `Center ${selectedCity.lat.toFixed(4)}, ${selectedCity.lon.toFixed(4)} · features stay inside ${selectedCity.name} bounds.`
                : 'No city selected — using raw lat/lon (features may spill into water on coastal centers).'}
            </p>
          </fieldset>

          <div>
            <label htmlFor="basemap" className="block text-sm font-medium text-atlas-ink mb-1">Basemap</label>
            <select
              id="basemap"
              value={basemap}
              onChange={(e) => setBasemap(e.target.value)}
              className="w-full border border-atlas-line rounded-md px-3 py-2 text-sm bg-white"
            >
              {Object.entries(BASEMAPS).map(([k, b]) => (
                <option key={k} value={k}>{b.label}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label htmlFor="radius" className="block text-sm font-medium text-atlas-ink">
                Map area
              </label>
              <span className="text-xs text-atlas-muted font-mono">
                {radiusScale.toFixed(2)}× &middot; {effectiveRadius} mi radius
              </span>
            </div>
            <input
              id="radius"
              type="range"
              min="0.25"
              max="3"
              step="0.05"
              value={radiusScale}
              onChange={(e) => setRadiusScale(parseFloat(e.target.value))}
              className="w-full accent-atlas-gold"
            />
            <div className="flex justify-between text-[10px] text-atlas-muted mt-0.5">
              <span>tight (0.25×)</span>
              <button
                type="button"
                onClick={() => setRadiusScale(1)}
                className="underline hover:text-atlas-ink"
                tabIndex={-1}
              >
                reset
              </button>
              <span>wide (3×)</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field id="lat" label="Latitude" value={lat} onChange={setLat} required inputMode="decimal" />
            <Field id="lon" label="Longitude" value={lon} onChange={setLon} required inputMode="decimal" />
          </div>
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-atlas-ink mb-1">
              Project description <span className="text-atlas-muted font-normal">(optional, 500 chars)</span>
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              rows={4}
              placeholder="One paragraph on what this map supports — the grant, the event, the decision. Shown alongside the map on the share page."
              className="w-full border border-atlas-line rounded-md px-3 py-2 text-sm"
            />
            <p className="mt-1 text-[11px] text-atlas-muted text-right">{notes.length} / 500</p>
          </div>
        </div>

        <button type="submit" disabled={!canGenerate} className="btn-primary w-full">
          {submitting ? 'Generating…' : 'Generate demo map'}
        </button>
        {submitError && <p className="text-xs text-red-600" role="alert">{submitError}</p>}
        {!canGenerate && !submitting && (
          <p className="text-xs text-atlas-muted" role="status">
            Required: organization, project title, valid lat/lon.
          </p>
        )}
      </form>

      <div className="card overflow-hidden min-h-[480px] lg:min-h-[640px] flex flex-col">
        <div className="px-4 py-2 border-b border-atlas-line text-xs uppercase tracking-wider text-atlas-muted bg-atlas-paper-2">
          Live preview
        </div>
        <div className="flex-1 relative">
          {previewData ? (
            <MapView
              data={previewData}
              center={[latNum, lonNum]}
              basemap={basemap}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-atlas-muted">
              Enter a valid latitude and longitude to see preview.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, value, onChange, required, maxLength, inputMode }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-atlas-ink mb-1">
        {label}{required && <span className="text-atlas-gold-dark"> *</span>}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        required={required}
        maxLength={maxLength}
        inputMode={inputMode}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-atlas-line rounded-md px-3 py-2 text-sm"
      />
    </div>
  );
}
